import { authFetch } from './authFetch';
import { createRequest, getUserId } from '../utils/hmac-utils';
import type { LeadAddress } from '../components/catering/data/types';

const BASE_URL = (process.env.REACT_APP_API_ENDPOINT_REVAMP || '').replace(/\/$/, '');

const CATERING_HMAC_ENABLED = true;

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function buildHeaders(token: string | null): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

async function cateringFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method || 'GET').toUpperCase();
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };

  if (!CATERING_HMAC_ENABLED || !MUTATION_METHODS.has(method)) {
    return authFetch(url, { ...init, headers });
  }

  headers['X-source'] = 'MP';
  let bodyObj: Record<string, unknown> = {};
  if (typeof init.body === 'string' && init.body.trim()) {
    try { bodyObj = JSON.parse(init.body) as Record<string, unknown>; } catch { bodyObj = {}; }
  }
  const userId = getUserId();
  const bodyWithUser = userId ? { ...bodyObj, userId } : bodyObj;
  const envelope = await createRequest(bodyWithUser);
  return authFetch(url, { ...init, method, headers, body: JSON.stringify(envelope) });
}

/** True when a JSON body is the { data, dataString, timestamp, nonce, signature } HMAC envelope. */
function isHmacEnvelope(json: unknown): json is { data: unknown } {
  return (
    !!json && typeof json === 'object' &&
    'data' in json && 'dataString' in json &&
    'signature' in json && 'nonce' in json && 'timestamp' in json
  );
}

async function handleOk<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let msg = fallback;
    try { msg = JSON.parse(text)?.error?.message || JSON.parse(text)?.message || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  const json = await res.json();
  return (isHmacEnvelope(json) ? json.data : json) as T;
}

// ── Shared pagination shape (used by estimates, orders, customers, invoices) ────

export interface PageMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

// ── Generic catering entity API ─────────────────────────────────────────────────

export const deleteCateringEntityApi = async (
  entity: 'leads' | 'estimates',
  id: string,
  locationId: string,
  token: string | null,
): Promise<{ success: boolean }> => {
  const params = new URLSearchParams({ locationId });
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/${entity}/${id}?${params}`, {
    method: 'DELETE',
    headers: buildHeaders(token),
  });
  return handleOk(res, `Failed to delete ${entity.slice(0, -1)}`);
};

// ════════════════════════════════════════════════════════════════════════════════
// LEADS API
// ════════════════════════════════════════════════════════════════════════════════

export interface ApiLeadListItem {
  id: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  address: LeadAddress | string | null;
  eventName: string | null;
  eventType: string | null;
  eventTypeOther: string | null;
  estimatedDate: string | null;
  estimatedTime: string | null;
  estimatedHeadcount: number | null;
  budgetMin: number | null;
  budgetMax: number | null;
  source: string | null;
  notes: string | null;
  status: string;
  lostReason: string | null;
  lostNote: string | null;
  flaggedAsSpam: boolean | null;
  lastContactedAt: string | null;
  followUpDate: string | null;
  linkedEstimates: Array<{
    id: string;
    estimateNumber: string | null;
    status: string;
    total: number;
    createdAt: string | null;
  }>;
  linkedOrders: Array<{
    id: string;
    orderId: string | null;
    status: string;
    total: number;
    createdAt: string | null;
  }>;
  convertedOrderId: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface ApiLeadDetail {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  address: LeadAddress | string | null;
  eventName: string | null;
  eventType: string | null;
  eventTypeOther: string | null;
  estimatedDate: string | null;
  estimatedTime: string | null;
  estimatedHeadcount: number | null;
  budgetMin: number | null;
  budgetMax: number | null;
  source: string | null;
  notes: string | null;
  status: string;
  lostReason: string | null;
  lostNote: string | null;
  followUpDate: string | null;
  estimateIds: string[];
  linkedEstimates: Array<{
    id: string;
    estimateNumber: string | null;
    status: string;
    total: number;
    createdAt: string | null;
  }>;
  linkedOrders: Array<{
    id: string;
    orderId: string | null;
    status: string;
    total: number;
    createdAt: string | null;
  }>;
  convertedOrderId: string | null;
  statusHistory: Array<{
    fromStatus: string | null;
    toStatus: string | null;
    changedBy: string;
    note: string | null;
    source: string | null;
    changedAt: string;
  }>;
  communicationLog: Array<{
    id: string;
    channel: string;
    subject: string | null;
    sentAt: string;
    sentBy: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface ApiCreateLeadResponse {
  success: boolean;
  leadId: string;
  status: string;
  createdAt: string;
}

export interface ApiTransitionStatusResponse {
  success: boolean;
  leadId: string;
  status: string;
  rejectedEstimateIds?: string[];
}

export interface ApiConvertLeadResponse {
  success: boolean;
  orderId?: string;
  orderNumber?: string;
  [key: string]: unknown;
}

export interface ListLeadsFilters {
  status?: string;
  source?: string;
  query?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

// ── Analytics response shapes ──────────────────────────────────────────────────

export interface LeadsAnalyticsMonthBucket {
  /** Pre-formatted label e.g. "Jan '25" */
  label: string;
  /** Calendar month 0–11 (JS index) */
  month: number;
  year: number;
  /** Non-draft leads created this month */
  total: number;
  won: number;
  lost: number;
  /** total - won - lost */
  active: number;
}

export interface LeadsAnalyticsFunnelRow {
  /** LeadStatus value, never 'draft' */
  status: string;
  count: number;
  /** count / totalNonDraft * 100 */
  pct: number;
}

export interface LeadsAnalyticsLostReason {
  /** Raw enum key e.g. 'budget_mismatch' */
  reason: string;
  count: number;
  /** count / totalLost * 100 */
  pct: number;
}

export interface LeadsAnalyticsConversion {
  id: string;
  /** lead.contactName (firstName + lastName) */
  name: string;
  company: string;
  /** ISO 8601 timestamp of the 'won' status_history entry */
  convertedDate: string;
  /** Human-readable order number e.g. "ORD-001"; null if no linked order */
  orderId: string | null;
  /** UUID primary key of the linked catering order */
  orderDbId: string | null;
  /** order.total from the linked catering order; 0 if no order */
  revenue: number;
}

export interface LeadsAnalyticsStatusCounts {
  [key: string]: number;
  /** All leads regardless of status — used for hub badge */
  all: number;
  new: number;
  contacted: number;
  estimate_sent: number;
  negotiating: number;
  won: number;
  lost: number;
}

export interface LeadsAnalyticsResponse {
  // KPI strip
  /** Non-draft leads created in current calendar month */
  kpiNewThisMonth: number;
  /** Math.round(won / totalNonDraft * 100) */
  kpiConversionRate: number;
  /** Active leads where followUpDate < now */
  kpiOverdueFollowUps: number;
  /** Unified pipeline value: sum of (budgetMin+budgetMax)/2 midpoint for active leads */
  pipelineValue: number;

  // Analytics summary
  totalThisMonth: number;
  /** % change vs prior month; 100 when prior=0 and this>0 */
  momChange: number;
  /** won / (won + lost) * 100 — different denominator from kpiConversionRate */
  analyticsConversionRate: number;
  wonCount: number;
  lostCount: number;
  /** Average days from createdAt to the 'won' status_history entry */
  avgDaysToConvert: number;
  /** Count of non-draft, non-won, non-lost leads */
  activeLeadCount: number;

  // Counts
  statusCounts: LeadsAnalyticsStatusCounts;

  // Charts — statusFunnel always has entries for all 6 non-draft statuses (count may be 0)
  statusFunnel: LeadsAnalyticsFunnelRow[];
  /** Sorted by count desc; empty array when no lost leads */
  lostReasons: LeadsAnalyticsLostReason[];
  /** Exactly 6 entries, oldest first */
  monthlyTrend: LeadsAnalyticsMonthBucket[];
  /** Top 10 won leads sorted by convertedDate desc, joined with catering orders */
  recentConversions: LeadsAnalyticsConversion[];
}

export interface LeadsPageMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
  totalEstimate: number;
}

export const listLeadsApi = async (
  locationId: string,
  token: string | null,
  filters: ListLeadsFilters = {},
): Promise<{ data: ApiLeadListItem[]; page: LeadsPageMeta }> => {
  const params = new URLSearchParams({ locationId });
  if (filters.status) params.set('status', filters.status);
  if (filters.source) params.set('source', filters.source);
  if (filters.query) params.set('query', filters.query);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.page != null) params.set('page', String(filters.page));
  if (filters.limit != null) params.set('limit', String(filters.limit));
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.order) params.set('order', filters.order);
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/leads?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch leads');
};

export const listLinkableLeadsApi = async (
  locationId: string,
  token: string | null,
): Promise<{ data: ApiLeadListItem[] }> => {
  const params = new URLSearchParams({ locationId });
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/leads/linkable?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch linkable leads');
};

// ── Kanban board (per-column pagination) ─────────────────────────────────────

export interface LeadsBoardColumn {
  status: string;
  items: ApiLeadListItem[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export interface LeadsBoardResponse {
  columns: LeadsBoardColumn[];
}

export interface ListLeadsBoardFilters {
  source?: string;
  query?: string;
  from?: string;
  to?: string;
  limit?: number;
}

function boardParams(locationId: string, filters: ListLeadsBoardFilters): URLSearchParams {
  const params = new URLSearchParams({ locationId });
  if (filters.source) params.set('source', filters.source);
  if (filters.query) params.set('query', filters.query);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.limit != null) params.set('limit', String(filters.limit));
  return params;
}

/** Initial board load: first page of every column + per-column totals. */
export const listLeadsBoardApi = async (
  locationId: string,
  token: string | null,
  filters: ListLeadsBoardFilters = {},
): Promise<LeadsBoardResponse> => {
  const params = boardParams(locationId, filters);
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/leads/board?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch leads board');
};

/** Infinite-scroll load-more for a single board column. */
export const listLeadsBoardColumnApi = async (
  locationId: string,
  token: string | null,
  status: string,
  page: number,
  filters: ListLeadsBoardFilters = {},
): Promise<LeadsBoardColumn> => {
  const params = boardParams(locationId, filters);
  params.set('status', status);
  params.set('page', String(page));
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/leads/board?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch board column');
};

export const getLeadDetailApi = async (
  id: string,
  token: string | null,
): Promise<ApiLeadDetail> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/leads/${id}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch lead detail');
};

export const createLeadApi = async (
  body: Record<string, unknown>,
  token: string | null,
): Promise<ApiCreateLeadResponse> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/leads`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to create lead');
};

export const updateLeadApi = async (
  id: string,
  body: Record<string, unknown>,
  token: string | null,
): Promise<unknown> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/leads/${id}`, {
    method: 'PATCH',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to update lead');
};

export const transitionLeadStatusApi = async (
  id: string,
  body: { locationId: string; status: string; lostReason?: string; note?: string },
  token: string | null,
): Promise<ApiTransitionStatusResponse> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/leads/${id}/status`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to transition lead status');
};

export const convertLeadToOrderApi = async (
  id: string,
  body: { locationId: string },
  token: string | null,
): Promise<ApiConvertLeadResponse> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/leads/${id}/convert`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to convert lead to order');
};

export interface ApiCustomerLookupResult {
  found: boolean;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  company?: string;
  source?: 'lead' | 'customer';
  leadId?: string;
  customerId?: string;
}

// export const getLeadsAnalyticsApi = async (
//   locationId: string,
//   token: string | null,
// ): Promise<LeadsAnalyticsResponse> => {
//   const params = new URLSearchParams({ locationId });
//   const res = await cateringFetch(`${BASE_URL}/api/v1/catering/leads/analytics?${params}`, {
//     headers: buildHeaders(token),
//   });
//   return handleOk(res, 'Failed to fetch leads analytics');
// };

/**
 * Look up a person by 10-digit phone number. The backend checks mh_customer
 * first, then falls back to mhc_leads scoped to `locationId` when provided.
 */
export const lookupCustomerByPhoneApi = async (
  digits: string,
  token: string | null,
  locationId?: string,
): Promise<ApiCustomerLookupResult> => {
  try {
    const params = new URLSearchParams({ phone: digits });
    if (locationId) params.set('locationId', locationId);
    const res = await cateringFetch(
      `${BASE_URL}/api/v1/catering/leads/lookup-customer?${params}`,
      { headers: buildHeaders(token) },
    );
    if (!res.ok) return { found: false };
    return res.json() as Promise<ApiCustomerLookupResult>;
  } catch {
    return { found: false };
  }
};

// ════════════════════════════════════════════════════════════════════════════════
// ESTIMATES API
// ════════════════════════════════════════════════════════════════════════════════

export interface ApiEstimateListItem {
  id: string;
  estimateNumber: string;
  customerFirstName: string | null;
  customerLastName: string | null;
  company: string | null;
  eventName: string | null;
  eventDate: string | null;
  headcount: number | null;
  total: string; // Prisma Decimal serialises as string
  status: string;
  validUntil: string | null;
  orderId: string | null;
  createdAt: string;
}

export interface ApiCreateEstimateResponse {
  success: boolean;
  estimateId: string;
  estimateNumber: string;
  leadId: string;
  status: string;
  createdAt: string;
}

export interface ApiUpdateEstimateResponse {
  success: boolean;
  estimateId: string;
  updatedAt: string;
}

/** Charge payload — pre-computed amount from FE. */
export interface ApiEstimateChargePayload {
  /** DB row id from mhc_applied_service_charges — sent for existing rows so the backend can do a targeted update. */
  id?: string;
  taxfeesId?: string;
  name: string;
  type?: 'PERCENT' | 'FLATFEE';
  value?: number;
  amount: number;
  isTaxable: boolean;
}

/** Tax payload — pre-computed amount from FE. */
export interface ApiEstimateTaxPayload {
  taxfeesId?: string;
  name: string;
  type?: 'PERCENT' | 'FLATFEE';
  value?: number;
  amount: number;
  isTaxable: boolean;
  isItemTax?: boolean;
  /** For item-level taxes: the item ids this tax was applied to. */
  itemIds?: string[];
}

export interface CreateEstimateBody {
  locationId: string;
  leadId?: string;
  customerFirstName?: string;
  customerLastName?: string;
  customerEmail?: string;
  customerPhone?: string;
  company?: string;
  address?: LeadAddress;
  eventName?: string;
  eventType?: string;
  eventTypeOther?: string;
  eventDate: string;
  eventTime: string;
  orderTypeId: string;
  headcount: number;
  validUntil?: string;
  lineItems?: UpdateLineItemBody[];
  discount?: { type?: string; value?: number; reason?: string };
  appliedCharges?: ApiEstimateChargePayload[];
  appliedTaxes?: ApiEstimateTaxPayload[];
  contractTerms?: string;
  instructions?: string;
  notes?: string;
}

export interface UpdateLineItemBody {
  type?: string;
  itemId?: string;
  tierId?: string;
  name?: string;
  unitPrice?: number;
  quantity?: number;
  notes?: string;
}

export interface UpdateEstimateBody {
  locationId: string;
  customerFirstName?: string;
  customerLastName?: string;
  customerEmail?: string;
  customerPhone?: string;
  company?: string;
  address?: LeadAddress;
  eventName?: string;
  eventType?: string;
  eventTypeOther?: string;
  eventDate?: string;
  eventTime?: string;
  headcount?: number;
  orderTypeId?: string;
  lineItems?: UpdateLineItemBody[];
  discount?: { type?: string; value?: number; reason?: string };
  appliedCharges?: ApiEstimateChargePayload[];
  appliedTaxes?: ApiEstimateTaxPayload[];
  validUntil?: string;
  contractTerms?: string;
  /** Delivery / event instructions — mirrors the order's instructions field. */
  instructions?: string;
  notes?: string;
}

export interface ListEstimatesFilters {
  status?: string;
  query?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export const listEstimatesApi = async (
  locationId: string,
  token: string | null,
  filters: ListEstimatesFilters = {},
): Promise<{ data: ApiEstimateListItem[]; page: PageMeta }> => {
  const params = new URLSearchParams({ locationId });
  if (filters.status) params.set('status', filters.status);
  if (filters.query) params.set('query', filters.query);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.page != null) params.set('page', String(filters.page));
  if (filters.limit != null) params.set('limit', String(filters.limit));
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.order) params.set('order', filters.order);
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/estimates?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch estimates');
};

export const createEstimateApi = async (
  body: CreateEstimateBody,
  token: string | null,
): Promise<ApiCreateEstimateResponse> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/estimates`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to create estimate');
};

export const updateEstimateApi = async (
  id: string,
  body: UpdateEstimateBody,
  token: string | null,
): Promise<ApiUpdateEstimateResponse> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/estimates/${id}`, {
    method: 'PATCH',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to update estimate');
};

export interface ApiEstimateDetail {
  id: string;
  estimateNumber: string;
  leadId: string | null;
  status: string;
  /** order_number of the linked mhc_orders row, or null if not converted. */
  orderId: string | null;
  customerFirstName: string | null;
  customerLastName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  company: string | null;
  address: LeadAddress | string | null;
  eventName: string | null;
  eventType: string | null;
  eventTypeOther: string | null;
  eventDate: string | null;
  eventTime: string | null;
  orderType: { id: string; name: string } | null;
  headcount: number | null;
  lineItems: {
    id: string;
    name: string | null;
    itemId: string | null;
    isCustomItem?: boolean;
    tierId: string | null;
    tierLabel: string | null;
    quantity: string | null;
    unitPrice: string | null;
    total: string | null;
    notes: string | null;
  }[];
  subtotal: string;
  discount: {
    type: string | null;
    value: string | null;
    amount: string | null;
    reason: string | null;
  };
  appliedCharges: {
    id: string;
    taxfeesId: string | null;
    name: string | null;
    type: string | null;
    value: number | null;
    amount: number | null;
    isTaxable: boolean;
  }[];
  appliedTaxes: {
    taxId: string;
    taxfeesId: string | null;
    name: string;
    type: string | null;
    rate: number | null;
    amount: number | null;
    isTaxable: boolean;
    isItemTax?: boolean;
    itemIds?: string[];
    exempt: boolean;
  }[];
  total: string;
  validUntil: string | null;
  contractTerms: string;
  instructions: string;
  notes: string;
  deposit: { type: string | null; value: string | null; dueDate: string | null };
  statusHistory: {
    fromStatus: string | null;
    toStatus: string | null;
    changedBy: string | null;
    note: string | null;
    source: string | null;
    changedAt: string;
  }[];
  createdAt: string;
  updatedAt: string | null;
}

export const getEstimateDetailApi = async (
  id: string,
  token: string | null,
): Promise<ApiEstimateDetail> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/estimates/${id}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch estimate detail');
};

export const getPublicEstimateDetailApi = async (id: string): Promise<ApiEstimateDetail> => {
  const res = await fetch(`${BASE_URL}/api/v1/catering/public/estimates/${id}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  return handleOk(res, 'Failed to fetch estimate detail');
};

// ── Catalog items (mhc_items + mhc_item_bulkprice) ────────────────────────────

export interface CatalogItemTier {
  id: string;
  unitLabel: string | null;
  unitPrice: number | null;
  serveCount: number | null;
  isDefault: boolean;
}

/** Item-level tax from mh_item_taxes → mh_taxfees (only present for non-default-tax items). */
export interface CatalogItemTax {
  id: string;
  name: string;
  rate: number;
  type: 'PERCENT' | 'FLATFEE';
}

export interface CatalogItem {
  id: string;
  name: string | null;
  price: number | null;
  serveCount: number | null;
  notes: string | null;
  minQtyRequired: number | null;
  maxQtyRequired: number | null;
  isCustomItem: boolean;
  /** true = item uses the location's default taxes; false = item carries its own `taxes`. */
  isDefaultItemTax: boolean;
  /** Item-specific taxes applied to this item only (empty when isDefaultItemTax). */
  taxes: CatalogItemTax[];
  bulkPriceTiers: CatalogItemTier[];
}

export interface SendEstimateBody {
  locationId: string;
  channels: ('email' | 'sms')[];
  subjectOverride?: string;
  bodyOverride?: string;
  phoneOverride?: string;
  /** Staff member who triggered the send — lets the backend attribute the resulting
   *  communicationLog entry's sentBy correctly when the JWT userId doesn't map cleanly. */
  staffId?: string;
}

export interface ApiSendEstimateResponse {
  success: boolean;
  estimateId: string;
  status: string;
  sentAt: string;
}

export const sendEstimateApi = async (
  id: string,
  body: SendEstimateBody,
  token: string | null,
): Promise<ApiSendEstimateResponse> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/estimates/${id}/send`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to send estimate');
};

export interface UpdateEstimateStatusBody {
  locationId: string;
  action: 'approve' | 'reject';
}

export interface ApiUpdateEstimateStatusResponse {
  success: boolean;
  estimateId: string;
  updatedAt: string;
}

export const updateEstimateStatusApi = async (
  id: string,
  body: UpdateEstimateStatusBody,
  token: string | null,
): Promise<ApiUpdateEstimateStatusResponse> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/estimates/${id}`, {
    method: 'PATCH',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to update estimate status');
};

export const fetchCatalogItemsApi = async (
  locationId: string,
  token: string | null,
): Promise<CatalogItem[]> => {
  const res = await cateringFetch(
    `${BASE_URL}/api/v1/catering/estimates/catalog-items?locationId=${encodeURIComponent(locationId)}`,
    { headers: buildHeaders(token) },
  );
  return handleOk<CatalogItem[]>(res, 'Failed to fetch catalog items');
};

// ════════════════════════════════════════════════════════════════════════════════
// ORDERS API
// ════════════════════════════════════════════════════════════════════════════════

export interface ApiOrderCustomer {
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  company: string | null;
  address: LeadAddress | string | null;
}

export interface ApiOrderFulfillment {
  date: string | null;
  time: string | null;
  address: LeadAddress | string | null;
}

export interface ApiOrderLineItem {
  id: string;
  itemId: string;
  /** true when this line is a custom item (synthetic mhc_items row, not in the catalog) */
  isCustomItem?: boolean;
  itemName: string | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  notes: string;
  tierId: string | null;
  tierLabel: string | null;
}

export interface ApiAppliedCharge {
  id: string;
  sourceChargeId: string | null;
  name: string | null;
  type: 'PERCENT' | 'FLATFEE' | null;
  value: number | null;
  amount: number;
  taxable: boolean;
  removed: boolean;
}

export interface ApiAppliedTax {
  id: string;
  taxId: string | null;
  name: string;
  type: 'PERCENT' | 'FLATFEE' | null;
  rate: number;
  amount: number;
  exempt: boolean;
  isItemTax?: boolean;
  itemIds?: string[];
  source: 'location_default' | 'order_type' | 'manual' | 'item';
  removed: boolean;
}

export interface ApiTaxPayload {
  /** ID from mh_taxfees (from get-taxes API). */
  taxfeesId?: string;
  name: string;
  type?: 'PERCENT' | 'FLATFEE';
  /** Configured rate (percentage or flat value). */
  value?: number;
  /** Pre-computed dollar amount from the frontend. */
  amount: number;
  /** Whether this tax's amount is included in the tax base (from get-taxes API isTaxable). */
  isTaxable: boolean;
  /** True for item-level taxes (mh_item_taxes), shown below default taxes. */
  isItemTax?: boolean;
  /** For item-level taxes: the item ids this tax was applied to. */
  itemIds?: string[];
}

export interface ApiInvoiceBlock {
  invoiceId: string;
  invoiceNumber: string | null;
  status: 'pending' | 'paid' | 'partial' | string;
  issuedAt: string | null;
  dueDate: string | null;
  schedule: Array<{
    id: string;
    label: string | null;
    dueAmount: number;
    dueDate: string;
    status: string;
  }>;
}

export interface ApiCancellationBlock {
  reason: string;
  note: string | null;
  refundDisposition: string | null;
  cancelledAt: string;
}

export interface ApiOrderListItem {
  id: string;
  orderNumber: string | null;
  status: string;
  leadId: string | null;
  customer: {
    firstName: string;
    lastName: string;
    company: string | null;
    email: string | null;
    phone: string | null;
  };
  orderType: string | null;
  orderTypeId: string | null;
  headcount: number | null;
  eventName: string | null;
  fulfillment: ApiOrderFulfillment;
  total: number;
  cacheTag: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApiOrderDetail {
  id: string;
  locationId: string;
  orderNumber: string | null;
  status: string;
  orderChannel: string;
  leadId: string | null;
  leadName: string | null;
  estimateId: string | null;
  customerId: string | null;
  customer: ApiOrderCustomer;
  eventName: string | null;
  eventType: string | null;
  orderType: string | null;
  orderTypeId: string | null;
  headcount: number | null;
  fulfillment: ApiOrderFulfillment;
  items: ApiOrderLineItem[];
  appliedCharges: ApiAppliedCharge[];
  appliedTaxes: ApiAppliedTax[];
  discount: { type: string; value: number | null; amount: number; reason: string | null };
  subtotal: number;
  taxAmount: number;
  total: number;
  internalNotes: string | null;
  instructions: string | null;
  contractTerms: string | null;
  contactPerson: string | null;
  beoTemplateId: string | null;
  beoData: Record<string, any> | null;
  invoice: ApiInvoiceBlock | null;
  cancellation: ApiCancellationBlock | null;
  statusHistory: Array<{
    preStatus: string | null;
    currentStatus: string | null;
    comments: string | null;
    createdAt: string | null;
    createdBy: string | null;
  }>;
  communicationLog: Array<{
    id: string;
    channel: string | null;
    templateId: string | null;
    recipientEmail: string | null;
    recipientPhone: string | null;
    subject: string | null;
    body: string;
    sentAt: string | null;
    sentBy: string | null;
  }>;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ApiOrderListResponse {
  data: ApiOrderListItem[];
  total: number;
  page?: PageMeta;
}

export interface ListOrdersFilters {
  status?: string;
  orderType?: string;
  from?: string;
  to?: string;
  q?: string;
  view?: 'list' | 'calendar';
  includeCancelled?: boolean;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

/** Full charge details sent from the frontend — backend persists as-is. */
export interface ApiChargePayload {
  sourceChargeId?: string;
  name: string;
  type?: 'PERCENT' | 'FLATFEE';
  value?: number;
  amount: number;
  taxable: boolean;
}

export interface CreateOrderBody {
  locationId: string;
  leadId?: string;
  customer?: {
    firstName: string;
    lastName: string;
    phone?: string;
    email?: string;
    company?: string;
    address?: LeadAddress;
  };
  estimateId?: string;
  orderTypeId: string;
  orderChannel?: 'portal' | 'merchant_app' | 'online';
  eventName?: string;
  eventType?: string;
  fulfillment: { date: string; time?: string; address?: LeadAddress };
  headcount?: number;
  items?: Array<{ itemId?: string; tierId?: string; quantity: number; notes?: string; name?: string; unitPrice?: number }>;
  appliedCharges?: ApiChargePayload[];
  appliedTaxes?: ApiTaxPayload[];
  discount?: { type: 'none' | 'percentage' | 'fixed'; value?: number; reason?: string };
  internalNotes?: string;
  instructions?: string;
  /** Carried over from the source estimate server-side when omitted. */
  contractTerms?: string;
  contactPerson?: string;
  beoTemplateId?: string;
  beoData?: Record<string, any>;
  allowPast?: boolean;
  status?: 'draft' | 'confirmed';
  /** Staff member who triggered this create — lets the backend attribute any resulting
   *  notification's sentBy correctly when the JWT userId doesn't map cleanly. */
  staffId?: string;
}

export interface UpdateOrderBody {
  locationId: string;
  customer?: { firstName?: string; lastName?: string; phone?: string; email?: string; company?: string };
  orderTypeId?: string;
  headcount?: number;
  eventName?: string;
  eventType?: string;
  fulfillment?: { date?: string; time?: string; address?: LeadAddress };
  discount?: { type: 'none' | 'percentage' | 'fixed'; value?: number; reason?: string };
  items?: Array<{ itemId?: string; tierId?: string; quantity: number; notes?: string; name?: string; unitPrice?: number }>;
  appliedCharges?: ApiChargePayload[];
  appliedTaxes?: ApiTaxPayload[];
  internalNotes?: string;
  instructions?: string;
  /** Carried over from the source estimate server-side when omitted. */
  contractTerms?: string;
  contactPerson?: string;
  beoTemplateId?: string;
  beoData?: Record<string, any>;
  status?: 'draft' | 'confirmed';
  /** Staff member who triggered this update — lets the backend attribute any resulting
   *  notification's sentBy correctly when the JWT userId doesn't map cleanly. */
  staffId?: string;
}

export interface RescheduleActionBody {
  action: 'reschedule';
  locationId: string;
  fulfillment: { date: string; time?: string; address?: LeadAddress };
  notifyCustomer?: boolean;
  leadTimeOverride?: boolean;
  /** Staff member who triggered this action — lets the backend attribute any resulting
   *  notification's sentBy correctly when the JWT userId doesn't map cleanly. */
  staffId?: string;
}

export interface CancelActionBody {
  action: 'cancel';
  locationId: string;
  reason: 'customer_request' | 'merchant_unavailable' | 'duplicate_order' | 'date_unavailable' | 'other';
  note?: string;
  refundDisposition?: 'refund_all' | 'refund_partial' | 'kept_as_credit' | 'kept_no_refund';
  notifyCustomer?: boolean;
  /** Staff member who triggered this action — lets the backend attribute any resulting
   *  notification's sentBy correctly when the JWT userId doesn't map cleanly. */
  staffId?: string;
}

export type OrderActionBody = RescheduleActionBody | CancelActionBody;

export interface TransitionStatusBody {
  locationId: string;
  status: 'preparing' | 'completed';
  /** Staff member who triggered this transition — lets the backend attribute any resulting
   *  notification's sentBy correctly when the JWT userId doesn't map cleanly. */
  staffId?: string;
}

export interface SendOrderBody {
  locationId: string;
  channels: ('email' | 'sms')[];
  subjectOverride?: string;
  bodyOverride?: string;
  phoneOverride?: string;
}

export interface ApiSendOrderResponse {
  success: boolean;
  orderId: string;
  sentAt: string;
}

export const listOrdersApi = async (
  locationId: string,
  token: string | null,
  filters: ListOrdersFilters = {},
): Promise<ApiOrderListResponse> => {
  const params = new URLSearchParams({ locationId });
  if (filters.status) params.set('status', filters.status);
  if (filters.orderType) params.set('orderType', filters.orderType);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.q) params.set('q', filters.q);
  if (filters.view) params.set('view', filters.view);
  if (filters.includeCancelled != null) params.set('includeCancelled', String(filters.includeCancelled));
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.order) params.set('order', filters.order);
  if (filters.page != null) params.set('page', String(filters.page));
  if (filters.pageSize != null) params.set('pageSize', String(filters.pageSize));
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/orders?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch orders');
};

export const getOrderDetailApi = async (
  id: string,
  locationId: string,
  token: string | null,
): Promise<ApiOrderDetail> => {
  const params = new URLSearchParams({ locationId });
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/orders/${id}?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch order detail');
};

export interface ApiOrderDayCapacity {
  date: string;
  confirmedOrderCount: number;
  confirmedHeadcount: number;
  maxOrdersPerDay: number | null;
  maxHeadcountPerDay: number | null;
}

export const getOrderDayCapacityApi = async (
  locationId: string,
  date: string,
  token: string | null,
  excludeOrderId?: string,
): Promise<ApiOrderDayCapacity> => {
  const params = new URLSearchParams({ locationId, date });
  if (excludeOrderId) params.set('excludeOrderId', excludeOrderId);
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/orders/capacity?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch day capacity');
};

export const createOrderApi = async (
  body: CreateOrderBody,
  token: string | null,
): Promise<ApiOrderDetail> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/orders`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to create order');
};

export const updateOrderApi = async (
  id: string,
  body: UpdateOrderBody,
  token: string | null,
  force: boolean = false,
): Promise<ApiOrderDetail | { warning: { code: string; message: string; requiresConfirmation: boolean } }> => {
  const params = new URLSearchParams();
  if (force) params.set('force', 'true');
  const url = `${BASE_URL}/api/v1/catering/orders/${id}${params.toString() ? `?${params}` : ''}`;
  const res = await cateringFetch(url, {
    method: 'PATCH',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to update order');
};

/**
 * Unified action endpoint — replaces v1.x /confirm + /reschedule + /cancel.
 * Body is a discriminated union on `action`.
 */
export const orderActionApi = async (
  id: string,
  body: OrderActionBody,
  token: string | null,
): Promise<ApiOrderDetail> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/orders/${id}/actions`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, `Failed to ${body.action} order`);
};

/**
 * Unified status endpoint — replaces v1.x /start-prep + /mark-ready
 * + /mark-out-for-delivery + /complete + /express-complete.
 * Only two targets supported: 'preparing' and 'completed'.
 */
export const sendOrderApi = async (
  id: string,
  body: SendOrderBody,
  token: string | null,
): Promise<ApiSendOrderResponse> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/orders/${id}/send`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to send order confirmation');
};

export const transitionOrderStatusApi = async (
  id: string,
  body: TransitionStatusBody,
  token: string | null,
): Promise<ApiOrderDetail> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/orders/${id}/status`, {
    method: 'PATCH',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to transition order status');
};

// ════════════════════════════════════════════════════════════════════════════════
// CUSTOMERS API
// ════════════════════════════════════════════════════════════════════════════════

export interface ApiCustomer {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  tags: string[] | null;
  dietaryNotes: string | null;
  totalOrders: number | null;
  totalRevenue: number | null;
  avgOrderValue: number | null;
  firstOrderDate: string | null;
  lastOrderDate: string | null;
  favoriteItems: string[] | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ApiCustomerListResponse {
  data: ApiCustomer[];
  total: number;
  page?: PageMeta;
}

export interface ApiUpdateCustomerResponse {
  success: boolean;
  customerId: string;
  updatedAt: string;
}

export interface ApiDeleteCustomerResponse {
  success: boolean;
}

export interface ListCustomersFilters {
  query?: string;
  tag?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export const listCustomersApi = async (
  locationId: string,
  token: string | null,
  filters: ListCustomersFilters = {},
): Promise<ApiCustomerListResponse> => {
  const params = new URLSearchParams({ locationId });
  if (filters.query) params.set('query', filters.query);
  if (filters.tag) params.set('tag', filters.tag);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.page != null) params.set('page', String(filters.page));
  if (filters.limit != null) params.set('limit', String(filters.limit));
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.order) params.set('order', filters.order);
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/customers?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch customers');
};

export const getCustomerDetailApi = async (
  id: string,
  locationId: string,
  token: string | null,
): Promise<ApiCustomer> => {
  const params = new URLSearchParams({ locationId });
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/customers/${id}?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch customer detail');
};

export interface UpdateCustomerBody {
  locationId: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  company?: string;
  dietaryNotes?: string;
  addedTags?: string[];
  removedTags?: string[];
}

export const updateCustomerApi = async (
  id: string,
  body: UpdateCustomerBody,
  token: string | null,
): Promise<ApiUpdateCustomerResponse> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/customers/${id}`, {
    method: 'PATCH',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to update customer');
};

export const deleteCustomerApi = async (
  id: string,
  locationId: string,
  token: string | null,
): Promise<ApiDeleteCustomerResponse> => {
  const params = new URLSearchParams({ locationId });
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/customers/${id}?${params}`, {
    method: 'DELETE',
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to delete customer');
};

// ════════════════════════════════════════════════════════════════════════════════
// INVOICES API
// ════════════════════════════════════════════════════════════════════════════════

export interface ApiInvoiceListItem {
  id: string;
  invoiceNumber: string;
  orderId: string;
  orderNumber: string | null;
  status: string;
  customerEmail: string;
  lineItemsTotal: string;
  discountAmount: string;
  subtotal: string;
  deliveryFee: string;
  serviceCharge: string;
  tax: string;
  total: string;
  amountPaid: string;
  outstandingBalance: string;
  nextDueDate: string | null;
  nextDueAmount: string | null;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiInvoiceDetail {
  id: string;
  invoiceNumber: string;
  orderId: string;
  orderNumber: string | null;
  status: string;
  customerEmail: string;
  customerProfileEmail?: string;
  lineItemsTotal: string;
  discountAmount: string;
  subtotal: string;
  deliveryFee: string;
  serviceCharge: string;
  tax: string;
  total: string;
  amountPaid: string;
  outstandingBalance: string;
  notes: string;
  sentAt: string | null;
  voidedAt: string | null;
  lineItems: {
    id: string;
    description: string;
    quantity: number;
    unitPrice: string;
    lineTotal: string;
  }[];
  appliedCharges: {
    id: string;
    name: string;
    type: string | null;
    rate: number | null;
    amount: number | null;
    taxable: boolean;
  }[];
  /** Itemized tax breakdown, stored as a snapshot at invoice creation/update time (backend
   *  confirmed: independent of the linked order's current state). Optional/possibly-empty:
   *  invoices created or edited via the simple `taxRate` path return `[]` here even with a
   *  nonzero `tax` total — only invoices created with itemized taxes have entries. `exempt` is
   *  currently always `false` server-side (exempt entries are discarded on write, not
   *  persisted with a zero amount). */
  appliedTaxes?: {
    id?: string;
    taxfeesId?: string | null;
    name: string;
    type: string | null;
    rate: number | null;
    amount: number | null;
    exempt?: boolean;
    isTaxable?: boolean;
    /** True for item-level taxes (mh_item_taxes), same distinction ApiOrderDetail
     *  carries — so an invoice generated from an order with item-level taxes can
     *  render/sort them identically to the Order Financial Summary. */
    isItemTax?: boolean;
  }[];
  paymentSchedule: {
    id: string;
    label: string;
    amount: string;
    dueDate: string;
    status: string;
    paidAt: string | null;
    paymentId?: string | null;
    amountPaid?: string;
    balance?: string;
  }[];
  payments: any[];
  communicationLog: {
    id: string;
    channel: string;
    templateKey: string;
    recipientEmail?: string;
    recipientPhone?: string;
    subject?: string;
    body: string;
    sentAt: string;
    sentBy: string;
  }[];
  statusHistory: {
    fromStatus: string | null;
    toStatus: string | null;
    changedBy: string | null;
    note: string | null;
    changedAt: string;
  }[];
  createdAt: string;
  updatedAt: string;
}

export interface ApiCreateInvoiceResponse {
  success: boolean;
  invoiceId: string;
  invoiceNumber: string;
  status: string;
  total: string;
  createdAt: string;
}

export interface ApiUpdateInvoiceResponse {
  success: boolean;
  invoiceId: string;
  status: string;
  updatedAt: string;
}

export interface ApiNotifyInvoiceResponse {
  success: boolean;
  invoiceId: string;
  status: string;
  issuedAt: string;
}

export interface InvoiceLineItemBody {
  description: string;
  quantity: number;
  unitPrice: number;
}

export interface InvoiceChargeBody {
  name: string;
  type: 'FLATFEE' | 'PERCENT';
  rate: number;
  amount?: number;
  taxable: boolean;
}

export interface InvoiceTaxBody {
  taxfeesId?: string;
  name: string;
  type: 'FLATFEE' | 'PERCENT';
  rate: number;
  amount: number;
  exempt: boolean;
  isTaxable?: boolean;
}

export interface InvoiceMilestoneBody {
  id?: string;
  label?: string;
  amount: number;
  dueDate: string;
}

export interface CreateInvoiceBody {
  locationId: string;
  orderId: string;
  customerEmail?: string;
  lineItems: InvoiceLineItemBody[];
  appliedCharges?: InvoiceChargeBody[];
  appliedTaxes?: InvoiceTaxBody[];
  taxRate?: number;
  taxExempt?: boolean;
  gratuity?: number;
  discountAmount?: number;
  paymentSchedule: InvoiceMilestoneBody[];
  notes?: string;
  sendImmediately?: boolean;
  channels?: ('email' | 'sms')[];
  phoneOverride?: string;
  /** Staff member who triggered this create — lets the backend attribute any resulting
   *  notification's sentBy correctly when the JWT userId doesn't map cleanly. */
  staffId?: string;
}

export interface UpdateInvoiceBody {
  locationId: string;
  action?: 'void' | 'mark_paid';
  customerEmail?: string;
  lineItems?: InvoiceLineItemBody[];
  appliedCharges?: InvoiceChargeBody[];
  appliedTaxes?: InvoiceTaxBody[];
  taxRate?: number;
  taxExempt?: boolean;
  gratuity?: number;
  paymentSchedule?: InvoiceMilestoneBody[];
  notes?: string;
  sendImmediately?: boolean;
  /** Staff member who triggered this update — lets the backend attribute any resulting
   *  notification's sentBy correctly when the JWT userId doesn't map cleanly. */
  staffId?: string;
}

export interface NotifyInvoiceBody {
  locationId: string;
  channels?: ('email' | 'sms')[];
  subjectOverride?: string;
  bodyOverride?: string;
  phoneOverride?: string;
  customerEmail?: string;
  /** Staff member who triggered this send — lets the backend attribute the resulting
   *  communicationLog entry's sentBy correctly when the JWT userId doesn't map cleanly. */
  staffId?: string;
}

export interface ListInvoicesFilters {
  status?: string;
  orderId?: string;
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export const listInvoicesApi = async (
  locationId: string,
  token: string | null,
  params?: ListInvoicesFilters,
): Promise<{ data: ApiInvoiceListItem[]; page?: PageMeta }> => {
  const q = new URLSearchParams({ locationId });
  if (params?.status) q.append('status', params.status);
  if (params?.orderId) q.append('orderId', params.orderId);
  if (params?.search) q.append('search', params.search);
  if (params?.from) q.append('from', params.from);
  if (params?.to) q.append('to', params.to);
  if (params?.page != null) q.append('page', String(params.page));
  if (params?.limit != null) q.append('limit', String(params.limit));
  if (params?.sort) q.append('sort', params.sort);
  if (params?.order) q.append('order', params.order);
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/invoices?${q}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch invoices');
};

export const createInvoiceApi = async (
  body: CreateInvoiceBody,
  token: string | null,
): Promise<ApiCreateInvoiceResponse> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/invoices`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to create invoice');
};

export const getInvoiceDetailApi = async (
  id: string,
  token: string | null,
): Promise<ApiInvoiceDetail> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/invoices/${id}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch invoice');
};

export const updateInvoiceApi = async (
  id: string,
  body: UpdateInvoiceBody,
  token: string | null,
): Promise<ApiUpdateInvoiceResponse> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/invoices/${id}`, {
    method: 'PATCH',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to update invoice');
};

export const notifyInvoiceApi = async (
  id: string,
  body: NotifyInvoiceBody,
  token: string | null,
): Promise<ApiNotifyInvoiceResponse> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/invoices/${id}/notify`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to send invoice notification');
};

export const sendPaymentReminderApi = async (
  id: string,
  locationId: string,
  token: string | null,
  /** Staff member who triggered this send — lets the backend attribute the resulting
   *  communicationLog entry's sentBy correctly when the JWT userId doesn't map cleanly. */
  staffId?: string,
): Promise<void> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/invoices/${id}/send-payment-reminder`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify({ locationId, ...(staffId ? { staffId } : {}) }),
  });
  await handleOk<unknown>(res, 'Failed to send payment reminder');
};

// ── Record payment / refund ─────────────────────────────────────────────────
export interface RecordPaymentBody {
  locationId: string;
  type?: 'PAYMENT' | 'REFUND';
  amount: number;              // positive magnitude; REFUND indicated by `type`
  method?: string;
  reference?: string;
  reason?: string;             // required for REFUND
  paymentScheduleId?: string;  // for REFUND: the schedule row (milestone) being reversed
  notes?: string;
  paymentDate?: string;        // ISO; defaults to now server-side
}

export interface ApiRecordedPayment {
  id: string;
  type: 'PAYMENT' | 'REFUND';
  paymentScheduleId?: string | null;
  amount: number;              // signed: PAYMENT positive, REFUND negative
  method: string;
  reference: string;
  reason: string | null;
  notes: string;
  recordedBy: string;
  date: string;
  isVoided: boolean;
  voidedAt: string | null;
}

export interface ApiRecordPaymentResponse {
  success: boolean;
  invoiceId: string;
  payment: ApiRecordedPayment;
  status: string;
  amountPaid: string;
  outstandingBalance: string;
  updatedAt: string;
}

export const recordInvoicePaymentApi = async (
  id: string,
  body: RecordPaymentBody,
  token: string | null,
): Promise<ApiRecordPaymentResponse> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/invoices/${id}/payments`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  return handleOk(res, 'Failed to record payment');
};

// ════════════════════════════════════════════════════════════════════════════════
// ORDER TYPES API
// ════════════════════════════════════════════════════════════════════════════════

export interface ApiOrderType {
  id: string;
  name: string;
  isEnabled: boolean;
}

export const listOrderTypesApi = async (
  locationId: string,
  token: string | null,
): Promise<{ data: ApiOrderType[] }> => {
  const params = new URLSearchParams({ locationId });
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/order-types?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch order types');
};

export interface ApiOrderTypeFee {
  id: string;
  name: string;
  type: 'PERCENT' | 'FLATFEE';
  rate: number;
  is_taxable: boolean;
}

export const getOrderTypeFeesApi = async (
  orderTypeId: string,
  locationId: string,
  token: string | null,
): Promise<ApiOrderTypeFee[]> => {
  const params = new URLSearchParams({ locationId });
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/order-types/${orderTypeId}/fees?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch order type fees');
};

// ════════════════════════════════════════════════════════════════════════════════
// SETTINGS API
// ════════════════════════════════════════════════════════════════════════════════

export interface ApiSettingResponse {
  key: string;
  value: string | null;
}

/** GET single key → { key, value } */
export const getSettingApi = async (
  locationId: string,
  key: string,
  token: string | null,
): Promise<ApiSettingResponse> => {
  const params = new URLSearchParams({ locationId, key });
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/settings?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch setting');
};

/** GET all settings for a location → { settings: Record<string, string|null>, orderTypes: ApiOrderType[] } */
export const getAllSettingsApi = async (
  locationId: string,
  token: string | null,
): Promise<{ settings: Record<string, string | null>; orderTypes?: ApiOrderType[] }> => {
  const params = new URLSearchParams({ locationId });
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/settings?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk(res, 'Failed to fetch settings');
};

/** POST — upsert multiple key-value pairs in one request */
export const upsertMultipleSettingsApi = async (
  locationId: string,
  entries: Array<{ key: string; value: string }>,
  token: string | null,
): Promise<{ success: boolean; saved: string[] }> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/settings`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify({ locationId, settings: entries }),
  });
  return handleOk(res, 'Failed to save settings');
};

// ════════════════════════════════════════════════════════════════════════════════
// COMMUNICATION TEMPLATES API
// ════════════════════════════════════════════════════════════════════════════════

export interface ApiCommTemplate {
  id: string;
  location_id: string;
  template_name: string | null;
  event_type: string;      // e.g. ESTIMATE_SENT, INVOICE_SENT, PAYMENT_REMINDER …
  channel: 'EMAIL' | 'SMS';
  subject: string | null;  // EMAIL only
  body: string;
  is_active: boolean;
  email_enabled: boolean;  // event-level flag: is the EMAIL channel enabled for this event type
  sms_enabled: boolean;    // event-level flag: is the SMS channel enabled for this event type
  tags: TemplateTag[];     // merge-field tags bundled with each template row
  created_at: string;
  updated_at: string;
}

export interface UpsertCommTemplatePayload {
  templateName?: string;
  eventType: string;
  channel: 'EMAIL' | 'SMS';
  subject?: string;        // EMAIL only
  body: string;
  isActive?: boolean;
  emailEnabled?: boolean;
  smsEnabled?: boolean;
}

/**
 * GET /api/v1/catering/communication-templates?locationId=xxx
 * Returns all template rows for the location.
 */
export const listCommTemplatesApi = async (
  locationId: string,
  token: string | null,
): Promise<ApiCommTemplate[]> => {
  const params = new URLSearchParams({ locationId });
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/communication-templates?${params}`, {
    headers: buildHeaders(token),
  });
  return handleOk<ApiCommTemplate[]>(res, 'Failed to fetch communication templates');
};

/**
 * POST /api/v1/catering/communication-templates
 * Upserts one row by UNIQUE(location_id, event_type, channel).
 * Returns an array (1 element on edit).
 * Throws with .status === 404 when catering is not configured for the location.
 */
export const upsertCommTemplateApi = async (
  locationId: string,
  payload: UpsertCommTemplatePayload,
  token: string | null,
): Promise<ApiCommTemplate[]> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/communication-templates`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify({ locationId, ...payload }),
  });
  if (res.status === 404) {
    throw Object.assign(new Error("Catering isn't set up for this location"), { status: 404 });
  }
  return handleOk<ApiCommTemplate[]>(res, 'Failed to save communication template');
};

/**
 * POST /api/v1/catering/communication-templates  (with restore: true)
 * Restores both EMAIL and SMS rows for the event type to their defaults.
 * Returns an array of 2 elements (email + SMS) with is_active unchanged.
 */
export const restoreCommTemplateApi = async (
  locationId: string,
  eventType: string,
  token: string | null,
): Promise<ApiCommTemplate[]> => {
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/communication-templates`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify({ locationId, eventType, restore: true }),
  });
  if (res.status === 404) {
    throw Object.assign(new Error("Catering isn't set up for this location"), { status: 404 });
  }
  return handleOk<ApiCommTemplate[]>(res, 'Failed to restore communication template');
};

// ── Merge-field tags (GET /tags?eventType=xxx) ────────────────────────────────

export interface TemplateTag {
  key: string;    // placeholder key, inserted as {{key}}
  label: string;  // human-readable label from the backend
}

export interface TemplateTagsResponse {
  event_type: string;
  entity: string | null;   // null for ad-hoc event types (tags is [])
  tags: TemplateTag[];
}

/**
 * GET /api/v1/catering/communication-templates/tags?eventType=xxx
 * Returns the merge-field tags available for an event type.
 * Ad-hoc event types return `entity: null` and `tags: []`.
 */
export const getTemplateTagsApi = async (
  eventType: string,
  token: string | null,
): Promise<TemplateTagsResponse> => {
  const params = new URLSearchParams({ eventType });
  const res = await cateringFetch(
    `${BASE_URL}/api/v1/catering/communication-templates/tags?${params}`,
    { headers: buildHeaders(token) },
  );
  return handleOk<TemplateTagsResponse>(res, 'Failed to fetch template tags');
};

// ════════════════════════════════════════════════════════════════════════════════
// NOTIFICATIONS API
// ════════════════════════════════════════════════════════════════════════════════

export interface ResolvedNotificationTemplate {
  country: string;
  email: { subject: string; body: string };
  sms: { body: string };
}

export type NotificationResolvableEntity = 'CATERING-ESTIMATE' | 'CATERING-INVOICE';

/**
 * GET /api/v1/catering/notifications/resolve-template
 * Returns the fully server-resolved email/SMS content for a notification —
 * no {{placeholder}} substitution needed on the client.
 * Throws with .status === 404 when the entity, its notification config, or its
 * email/sms template isn't found for the given location.
 */
export const resolveNotificationTemplateApi = async (
  entity: NotificationResolvableEntity,
  id: string,
  country: string,
  token: string | null,
): Promise<ResolvedNotificationTemplate> => {
  const params = new URLSearchParams({ entity, id, country });
  const res = await cateringFetch(`${BASE_URL}/api/v1/catering/notifications/resolve-template?${params}`, {
    headers: buildHeaders(token),
  });
  if (res.status === 404) {
    throw Object.assign(new Error('Notification template is not configured'), { status: 404 });
  }
  return handleOk<ResolvedNotificationTemplate>(res, 'Failed to resolve notification template');
};

// ════════════════════════════════════════════════════════════════════════════════
// TAXES API
// ════════════════════════════════════════════════════════════════════════════════

export interface TaxFeeItem {
  id: string;
  name: string;
  type: 'PERCENT' | 'FLATFEE';
  rate: number;
  /** true = real tax → TaxEditor; false = fee/charge → ChargesEditor */
  isDefault: boolean;
  /** true = include this fee's amount in the tax base when computing taxes */
  isTaxable: boolean;
}

export interface GetTaxesResponse {
  items: TaxFeeItem[];
}

export const fetchTaxesApi = async (
  orderTypeId: string,
  token: string | null,
): Promise<GetTaxesResponse> => {
  const params = new URLSearchParams({ orderTypeId });
  const res = await fetch(`${BASE_URL}/api/v1/catering/taxes?${params.toString()}`, {
    headers: buildHeaders(token),
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(`Failed to fetch taxes: ${msg}`);
  }
  return res.json();
};
